import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
} from "@/components/cathedral/Primitives";

export default function Directives() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [lockedOnly, setLockedOnly] = useState(false);

  useEffect(() => {
    api.directives({}).then((r) => setItems(r.directives || []));
  }, []);

  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((d) => d.category && s.add(d.category));
    return Array.from(s).sort();
  }, [items]);

  const visible = useMemo(() => {
    let f = items;
    if (lockedOnly) f = f.filter((d) => d.locked);
    if (category) f = f.filter((d) => d.category === category);
    if (q.trim()) {
      const needle = q.toLowerCase();
      f = f.filter((d) =>
        [d.id, d.name, d.text, d.category].filter(Boolean).join(" ").toLowerCase().includes(needle)
      );
    }
    return f;
  }, [items, q, category, lockedOnly]);

  return (
    <section data-testid="directives-page" className="px-8 lg:px-12 py-12 max-w-screen-2xl">
      <SectionHeader
        overline="73 Sacred Directives · Cathedral Edition v1.1"
        title="The Lattice"
        description="DIR-000 is the gate. DIR-071 / DIR-072 / DIR-073 are the non-removable anchors. The remaining directives form the body of the law."
      />

      <Card className="p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            data-testid="directives-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search directives..."
            className="border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
          />
          <select
            data-testid="directives-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border border-[var(--chrome)] rounded-none p-3 font-mono text-[11px] uppercase tracking-widest bg-[var(--void-2)]"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <label className="flex items-center gap-3 border border-[var(--chrome)] p-3 font-mono text-[11px] uppercase tracking-widest cursor-pointer">
            <input
              type="checkbox"
              data-testid="directives-locked-only"
              checked={lockedOnly}
              onChange={(e) => setLockedOnly(e.target.checked)}
              className="accent-[var(--cyan)]"
            />
            Locked anchors only
          </label>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {visible.map((d) => (
          <Card key={d.id} className="p-8" data-testid={`directive-${d.id}`}>
            <div className="flex items-center justify-between mb-3">
              <Overline>{d.id}</Overline>
              <div className="flex items-center gap-2">
                {d.locked && <Tag tone="pass">LOCKED</Tag>}
                {d.category && <Tag>{d.category}</Tag>}
              </div>
            </div>
            <h3 className="font-serif text-2xl font-light tracking-tight text-[var(--text)] leading-tight">
              {d.name}
            </h3>
            {d.text && (
              <p className="mt-4 text-sm text-[var(--text-2)] leading-relaxed">
                {d.text}
              </p>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
